import { baseURL } from './usermanagement';

/**
 * Get the best profile photo using priority logic
 * @param {Object} userobj - User object from sessionStorage
 * @returns {string} - Best available profile photo URL
 */
export const getBestProfilePhoto = (userobj) => {
    if (!userobj) return `${baseURL}/file/avatar/1.webp`;
    
    // Profile photo priority: profile_url (from profile settings) > profile_photo_fullurl (OAuth) > avatar_url > profile_picture_url > default
    return userobj.profile_photo_url_object?.profile_url ? 
           `${baseURL}/file/avatar/${userobj.profile_photo_url_object.profile_url}` :
           userobj.profile_photo_url_object?.profile_photo_fullurl || 
           userobj.avatar_url || 
           userobj.profile_picture_url || 
           `${baseURL}/file/avatar/1.webp`;
};

/**
 * Get profile photo with fallback to sessionStorage when API returns null values
 * @param {string} apiUrl - API profile photo URL
 * @param {string} fallbackKey - Type of fallback ('avatar' or 'profile')
 * @returns {string} - Best available profile photo URL
 */
export const getProfilePhotoWithFallback = (apiUrl, fallbackKey) => {
    // If API returns a valid URL, use it
    if (apiUrl && apiUrl !== 'null' && apiUrl !== null) {
        return apiUrl;
    }
    
    // Otherwise, try to get from sessionStorage user object
    try {
        const userobj = JSON.parse(sessionStorage.getItem("user") || "{}");
        if (userobj && userobj.profile_photo_url_object?.profile_photo_fullurl) {
            return userobj.profile_photo_url_object.profile_photo_fullurl;
        }
    } catch (error) {
        console.warn('Error parsing sessionStorage user data:', error);
    }
    
    // Final fallback
    return fallbackKey === 'avatar' ? `${baseURL}/file/avatar/1.webp` : `${baseURL}/file/product/image/aa6a16aa87d64a76ad37b77060a2a224.webp`;
};

/**
 * Get activity profile photo using priority logic
 * @param {Object} activity - Activity object
 * @returns {string} - Best available profile photo URL for the activity
 */
export const getActivityProfilePhoto = (activity) => {
    if (!activity) {
        return `${baseURL}/file/product/image/aa6a16aa87d64a76ad37b77060a2a224.webp`;
    }
    
    // Check if activity has profile_photo_url_object
    if (!activity.profile_photo_url_object) {
        // Try to find photo fields directly on the activity object
        if (activity.profile_photo_fullurl && activity.profile_photo_fullurl !== 'null' && activity.profile_photo_fullurl !== null) {
            return activity.profile_photo_fullurl;
        }
        
        if (activity.profile_photo && activity.profile_photo !== 'null' && activity.profile_photo !== null) {
            return activity.profile_photo;
        }
        
        if (activity.avatar_url && activity.avatar_url !== 'null' && activity.avatar_url !== null) {
            return activity.avatar_url;
        }
        
        if (activity.profile_picture_url && activity.profile_picture_url !== 'null' && activity.profile_picture_url !== null) {
            return activity.profile_picture_url;
        }
        
        // Try to get from sessionStorage user object as fallback
        try {
            const userobj = JSON.parse(sessionStorage.getItem("user") || "{}");
            if (userobj && userobj.profile_photo_url_object?.profile_photo_fullurl) {
                return userobj.profile_photo_url_object.profile_photo_fullurl;
            }
        } catch (error) {
            console.warn('Error parsing sessionStorage user data:', error);
        }
        
        return `${baseURL}/file/product/image/aa6a16aa87d64a76ad37b77060a2a224.webp`;
    }
    
    const photoObj = activity.profile_photo_url_object;
    
    // Check if ALL API photo fields are null (this is the key scenario)
    const allFieldsNull = !photoObj.profile_url && !photoObj.fullsize_url && !photoObj.avatar_url && 
                         !photoObj.profile_picture_url && !photoObj.profile_photo_fullurl;
    
    if (allFieldsNull) {
        // Try to get from sessionStorage user object
        try {
            const userobj = JSON.parse(sessionStorage.getItem("user") || "{}");
            if (userobj && userobj.profile_photo_url_object?.profile_photo_fullurl) {
                return userobj.profile_photo_url_object.profile_photo_fullurl;
            }
        } catch (error) {
            console.warn('Error parsing sessionStorage user data:', error);
        }
        
        return `${baseURL}/file/product/image/aa6a16aa87d64a76ad37b77060a2a224.webp`;
    }
    
    // Priority 1: Check if API has valid profile_url
    if (photoObj.profile_url && photoObj.profile_url !== 'null' && photoObj.profile_url !== null) {
        return `${baseURL}/file/avatar/${photoObj.profile_url}`;
    }
    
    // Priority 2: Check if API has profile_photo_fullurl (Google OAuth)
    if (photoObj.profile_photo_fullurl && photoObj.profile_photo_fullurl !== 'null' && photoObj.profile_photo_fullurl !== null) {
        return photoObj.profile_photo_fullurl;
    }
    
    // Priority 3: Check if API has other photo URLs
    if (photoObj.fullsize_url && photoObj.fullsize_url !== 'null' && photoObj.fullsize_url !== null) {
        return `${baseURL}/file/avatar/${photoObj.fullsize_url}`;
    }
    
    if (photoObj.avatar_url && photoObj.avatar_url !== 'null' && photoObj.avatar_url !== null) {
        return photoObj.avatar_url;
    }
    
    if (photoObj.profile_picture_url && photoObj.profile_picture_url !== 'null' && photoObj.profile_picture_url !== null) {
        return photoObj.profile_picture_url;
    }
    
    // Final fallback: default avatar
    return `${baseURL}/file/product/image/aa6a16aa87d64a76ad37b77060a2a224.webp`;
};

/**
 * Get notification item profile photo using priority logic
 * @param {Object} item - Notification item object
 * @returns {string|Object} - Best available profile photo URL or fallback element
 */
export const getNotificationProfilePhoto = (item) => {
    // If item has profile_photo_url_object with valid profile_url, use it
    if (item.profile_photo_url_object && item.profile_photo_url_object.profile_url && 
        item.profile_photo_url_object.profile_url !== 'null' && item.profile_photo_url_object.profile_url !== null) {
        return `${baseURL}/file/avatar/${item.profile_photo_url_object.profile_url}`;
    }
    
    // If item has profile_photo_url_object but profile_url is null, try sessionStorage fallback
    if (item.profile_photo_url_object) {
        try {
            const userobj = JSON.parse(sessionStorage.getItem("user") || "{}");
            if (userobj && userobj.profile_photo_url_object?.profile_photo_fullurl) {
                return userobj.profile_photo_url_object.profile_photo_fullurl;
            }
        } catch (error) {
            console.warn('Error parsing sessionStorage user data:', error);
        }
    }
    
    // Return null to indicate fallback should be used
    return null;
}; 